import type { InternalLayer } from 'hooks/internalLayer/types';
import { LayerFeaturesContent } from './LayerFeaturesContent';
import { Layer } from './Layer';
import { useUscgMapContext } from './UscgMapContext';
import { useMemo, useState } from 'react';
import { useDeleteLocation } from 'hooks/icsLocation/useDeleteLocation';
import { usePublishInternalLayerMutation } from 'hooks/internalLayer/usePublishInternalLayerMutation';
import { useApproveInternalLayerMutation } from 'hooks/internalLayer/useApproveInternalLayerMutation';
import { selectUserGuid, useAppSelector } from 'slices/commonSelectors';
import {
  fetchInternalLayerVersions,
  useListInternalLayerVersionsQuery,
} from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import {
  canPublishInternalLayer,
  InternalLayerVersionBanner,
} from './InternalLayerVersionBanner';
import { LayerPermissionsDialog } from './LayerPermissionsDialog';
import { PublishToOrganizationDialog } from './PublishToOrganizationDialog';
import {
  InternalLayerDetailsFields,
  InternalLayerNameEditor,
} from './InternalLayerDetailsFields';
import { useWorkspaceName } from './useWorkspaceName';

function InternalLayerListItemContent({ layer }: { layer: InternalLayer }) {
  const { getSelectedVersionForLayer, setSelectedInternalLayerVersion } =
    useUscgMapContext();
  const activeVersionId = getSelectedVersionForLayer(layer.id)?.id;
  const deleteLocation = useDeleteLocation();

  const handleDeleteLocation = async (objectId: string) => {
    const response = await deleteLocation.mutateAsync(objectId);
    const versionId = response.data.internalLayerVersionId;
    if (!versionId) return;
    const versions = await fetchInternalLayerVersions({
      filter: { internalLayerId: layer.id },
    });
    const match = versions.data.find((version) => version.id === versionId);
    if (match) setSelectedInternalLayerVersion(layer.id, match);
  };

  const { data } = useListInternalLayerVersionsQuery({
    filter: {
      internalLayerId: layer.id,
    },
  });
  const versions = useMemo(() => data?.data ?? [], [data?.data]);
  const activeVersion = versions.find((v) => v.id === activeVersionId);
  const canChangeDraft =
    layer.publishedAt != null || Boolean(layer.viewerCanEdit);
  const canEditVersion =
    activeVersion?.approval?.approvalStatus === 'DRAFT' && canChangeDraft;

  return (
    <>
      <InternalLayerVersionBanner layer={layer} />
      <InternalLayerDetailsFields
        key={`${layer.id}:${layer.updatedAt}`}
        layer={layer}
      />
      <LayerFeaturesContent
        layer={layer}
        layerType="internal"
        showAddObjectButton={canChangeDraft}
        onDelete={canEditVersion ? handleDeleteLocation : undefined}
      />
    </>
  );
}

type InternalLayerItemProps = {
  layer: InternalLayer;
  onDelete?: () => void;
  onZoomToLayer?: () => void;
  onFocusView?: () => void;
  defaultExpanded?: boolean;
  /** `data-tour` on the layer row for product tours. */
  dataTour?: string;
  /** `data-tour` on the zoom control for product tours. */
  zoomTour?: string;
};

export function InternalLayerItem({
  layer,
  onDelete,
  onZoomToLayer,
  onFocusView,
  defaultExpanded = false,
  dataTour,
  zoomTour,
}: InternalLayerItemProps) {
  const { getSelectedVersionForLayer } = useUscgMapContext();
  const versionId = getSelectedVersionForLayer(layer.id)?.id;
  const userGuid = useAppSelector(selectUserGuid);
  const publishLayer = usePublishInternalLayerMutation();
  const approveLayer = useApproveInternalLayerMutation();
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const canChangeDraft =
    layer.publishedAt != null || Boolean(layer.viewerCanEdit);
  const showPublish = canPublishInternalLayer(layer, userGuid);
  const publishToOrganization = Boolean(layer.incidentId);
  const workspaceName = useWorkspaceName(
    layer.incidentId,
    layer.incident?.name
  );

  return (
    <>
      <Layer.Item
        eventKey={`internal-${layer.id}`}
        defaultExpanded={defaultExpanded}
        dataTour={dataTour}
        header={
          <Layer.Header
            title={layer.name}
            nameEditor={<InternalLayerNameEditor layer={layer} />}
            workspaceName={workspaceName}
            layerId={layer.id}
            layerType="internal"
            onZoomToLayer={onZoomToLayer}
            zoomTour={zoomTour}
            onFocusView={onFocusView}
            onPermissions={() => setPermissionsOpen(true)}
            onDelete={canChangeDraft ? onDelete : undefined}
            deleteLabel="Archive"
            onApprove={
              layer.incidentId && !layer.approvedAt
                ? () => approveLayer.mutate({ internalLayerId: layer.id })
                : undefined
            }
            onPublish={
              showPublish
                ? publishToOrganization
                  ? () => setPublishOpen(true)
                  : () => publishLayer.mutate({ internalLayerId: layer.id })
                : undefined
            }
            publishLabel={
              publishToOrganization ? 'Publish to Organization' : 'Publish'
            }
            publishDisabled={publishLayer.isPending}
            pickPayload={{
              name: layer.name || 'Untitled internal layer',
              versionId: versionId
                ? String(versionId)
                : layer.draftVersionId
                  ? String(layer.draftVersionId)
                  : undefined,
            }}
          />
        }
      >
        <Layer.Content>
          <InternalLayerListItemContent layer={layer} />
        </Layer.Content>
      </Layer.Item>
      <LayerPermissionsDialog
        layer={layer}
        open={permissionsOpen}
        onClose={() => setPermissionsOpen(false)}
      />
      {publishToOrganization ? (
        <PublishToOrganizationDialog
          layer={layer}
          open={publishOpen}
          onClose={() => setPublishOpen(false)}
        />
      ) : null}
    </>
  );
}
